import { useQuery } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { getBillingInfo, getUsagePercent, isAtLimit, isUnlimited } from '@/lib/billing'
import type { BillingInfo, QuotaType } from '@/types/billing'

export function useUsage() {
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()

    return useQuery<BillingInfo>({
        queryKey: ['billing', 'usage', orgSlug],
        queryFn: () => getBillingInfo(orgSlug as string),
        enabled: !!orgSlug,
        staleTime: 30000, // 30 seconds
    })
}

export function useQuotaCheck() {
    const { data: billing, isLoading } = useUsage()

    const canAddTeamMember = (): boolean => {
        if (!billing || isLoading) return true
        return !isAtLimit(billing.usage.active_team_members, billing.limits.active_team_members)
    }

    const canAddJob = (): boolean => {
        if (!billing || isLoading) return true
        return !isAtLimit(billing.usage.active_jobs, billing.limits.active_jobs)
    }

    const canAddCustomer = (): boolean => {
        if (!billing || isLoading) return true
        return !isAtLimit(billing.usage.active_customers, billing.limits.active_customers)
    }

    const getUsagePercentForType = (type: QuotaType): number => {
        if (!billing) return 0

        switch (type) {
            case 'team':
                return getUsagePercent(
                    billing.usage.active_team_members,
                    billing.limits.active_team_members
                )
            case 'jobs':
                return getUsagePercent(billing.usage.active_jobs, billing.limits.active_jobs)
            case 'customers':
                return getUsagePercent(
                    billing.usage.active_customers,
                    billing.limits.active_customers
                )
        }
    }

    const isUnlimitedForType = (type: QuotaType): boolean => {
        if (!billing) return false

        switch (type) {
            case 'team':
                return isUnlimited(billing.limits.active_team_members)
            case 'jobs':
                return isUnlimited(billing.limits.active_jobs)
            case 'customers':
                return isUnlimited(billing.limits.active_customers)
        }
    }

    const getUsageForType = (type: QuotaType): { current: number; limit: number } => {
        if (!billing) return { current: 0, limit: 0 }

        switch (type) {
            case 'team':
                return {
                    current: billing.usage.active_team_members,
                    limit: billing.limits.active_team_members,
                }
            case 'jobs':
                return {
                    current: billing.usage.active_jobs,
                    limit: billing.limits.active_jobs,
                }
            case 'customers':
                return {
                    current: billing.usage.active_customers,
                    limit: billing.limits.active_customers,
                }
        }
    }

    return {
        billing,
        isLoading,
        canAddTeamMember,
        canAddJob,
        canAddCustomer,
        getUsagePercent: getUsagePercentForType,
        isUnlimited: isUnlimitedForType,
        getUsage: getUsageForType,
    }
}

export function usePlanInfo() {
    const { data: billing, isLoading } = useUsage()

    return {
        plan: billing?.plan,
        subscription: billing?.subscription,
        isLoading,
        isPro: billing?.plan?.id === 'pro',
        isEnterprise: billing?.plan?.id === 'enterprise',
        isFree: billing?.plan?.id === 'free',
        isTrialing: billing?.subscription?.status === 'trialing',
        isPastDue: billing?.subscription?.status === 'past_due',
        willCancel: billing?.subscription?.cancel_at_period_end ?? false,
    }
}
